import {
  CaseFilters,
  CaseRecord,
  ColumnConfig,
  ColumnKey,
  FilterOptions,
  SortOrder,
} from "../types/App.types";

export const ALL_FILTER_VALUE = "all";
export const MISSING_FILTER_VALUE = "__missing__";

export const emptyFilters = (): CaseFilters => ({
  city: ALL_FILTER_VALUE,
  state: ALL_FILTER_VALUE,
  sportAffiliation: ALL_FILTER_VALUE,
  adjudicatingBody: ALL_FILTER_VALUE,
});

export const getColumnLabel = (
  columns: ColumnConfig[],
  key: ColumnKey,
): string => columns.find((c) => c.key === key)?.label ?? key;

export const buildFilterOptions = (rows: CaseRecord[]): FilterOptions => {
  const cities = new Set<string>();
  const states = new Set<string>();
  const sportAffiliations = new Set<string>();
  const adjudicatingBodies = new Set<string>();

  rows.forEach((row) => {
    collectOption(cities, row.city);
    collectOption(states, row.state);
    collectOption(adjudicatingBodies, row.adjudicatingBody);
    splitMultiValue(row.sportAffiliations).forEach((affiliation) =>
      collectOption(sportAffiliations, affiliation),
    );
  });

  return {
    cities: Array.from(cities).sort(),
    states: Array.from(states).sort(),
    sportAffiliations: Array.from(sportAffiliations).sort(),
    adjudicatingBodies: Array.from(adjudicatingBodies).sort(),
  };
};

export const filterCaseRows = (
  rows: CaseRecord[],
  searchTerm: string,
  filters: CaseFilters,
): CaseRecord[] => {
  const normalizedSearch = normalize(searchTerm);

  return rows.filter((row) => {
    if (!matchesFilter(row.city, filters.city)) {
      return false;
    }
    if (!matchesFilter(row.state, filters.state)) {
      return false;
    }
    if (!matchesFilter(row.adjudicatingBody, filters.adjudicatingBody)) {
      return false;
    }
    if (
      !matchesSportAffiliation(row.sportAffiliations, filters.sportAffiliation)
    ) {
      return false;
    }

    return !normalizedSearch || searchableText(row).includes(normalizedSearch);
  });
};

export const sortCaseRows = (
  rows: CaseRecord[],
  sortOrder: SortOrder,
): CaseRecord[] => {
  const direction = sortOrder.direction === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    if (sortOrder.name === "dateOfIssuance") {
      const aTime = Date.parse(a.dateOfIssuance);
      const bTime = Date.parse(b.dateOfIssuance);
      const aMissing = Number.isNaN(aTime);
      const bMissing = Number.isNaN(bTime);

      // Keep missing/invalid dates at the end for either sort direction.
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;

      return (aTime - bTime) * direction;
    }

    const comparison = compareStrings(
      String(a[sortOrder.name as ColumnKey] ?? ""),
      String(b[sortOrder.name as ColumnKey] ?? ""),
    );

    return comparison * direction;
  });
};

export const paginateRows = (
  rows: CaseRecord[],
  page: number,
  rowsPerPage: number,
): CaseRecord[] => {
  const start = page * rowsPerPage;
  return rows.slice(start, start + rowsPerPage);
};

export const displayValue = (value: string | null | undefined): string => {
  const normalized = value?.trim() ?? "";
  return normalized && normalized !== "Not provided"
    ? normalized
    : "Not provided";
};

const collectOption = (options: Set<string>, value: string): void => {
  const option = value.trim();
  if (option && option !== "Not provided") {
    options.add(option);
  }
};

const splitMultiValue = (value: string): string[] => {
  const delimiter = value.includes(";") ? ";" : ",";
  return value
    .split(delimiter)
    .map((item) => item.trim())
    .filter(Boolean);
};

const searchableText = (row: CaseRecord): string =>
  normalize(
    [
      row.name,
      row.city,
      row.state,
      row.sportAffiliations,
      row.misconduct,
      row.actionTaken,
      row.additionalDetail,
      row.adjudicatingBody,
    ].join(" "),
  );

const compareStrings = (a: string, b: string): number => {
  const aMissing = isMissing(a);
  const bMissing = isMissing(b);

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  return a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const isMissing = (value: string): boolean =>
  !value.trim() || normalize(value) === "not provided";

const matchesFilter = (value: string, filter: string): boolean => {
  if (filter === ALL_FILTER_VALUE) return true;
  if (filter === MISSING_FILTER_VALUE) return isMissing(value);
  return value === filter;
};

const matchesSportAffiliation = (value: string, filter: string): boolean => {
  if (filter === ALL_FILTER_VALUE) return true;
  if (filter === MISSING_FILTER_VALUE) return isMissing(value);
  return normalize(value).includes(normalize(filter));
};

const normalize = (value: string): string => value.trim().toLowerCase();
